import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/* Low-level, token-styled UI atoms. Intentionally minimal — the Claude.ai
 * design system replaces these implementations while keeping the same props. */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'ai';

const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-neutral-10 text-neutral-0 hover:bg-neutral-9 dark:bg-neutral-11 dark:text-neutral-0',
  secondary: 'bg-surface border border-border-default text-text-primary hover:bg-hover',
  ghost: 'text-text-secondary hover:bg-hover hover:text-text-primary',
  ai: 'bg-ai text-white hover:bg-ai-soft shadow-ai',
};

export function Button({
  variant = 'secondary',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      className={cn(
        'inline-flex h-control shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium',
        'transition-colors duration-fast ease-standard disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-1 focus-visible:ring-offset-canvas',
        buttonVariants[variant],
        className,
      )}
      {...props}
    />
  );
}

export function Card({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-md border border-border-subtle bg-surface shadow-sm', className)}
      {...props}
    >
      {children}
    </div>
  );
}

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'compliance' | 'ai' | 'engine';

const badgeTones: Record<BadgeTone, string> = {
  neutral: 'bg-inset text-text-secondary border-border-subtle',
  success: 'bg-success/10 text-success border-success/20',
  warning: 'bg-warning/10 text-warning border-warning/20',
  danger: 'bg-danger/10 text-danger border-danger/20',
  compliance: 'bg-compliance/10 text-compliance border-compliance/20',
  ai: 'bg-ai/10 text-ai border-ai/25',
  engine: 'bg-engine-wash text-engine border-engine/20',
};

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: BadgeTone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium leading-none',
        badgeTones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** A thin engine identity keyline — the only place engine accent shows as edge. */
export function EngineKeyline({ className }: { className?: string }) {
  return <div className={cn('h-0.5 w-full rounded-full bg-engine', className)} aria-hidden />;
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text-primary">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-text-secondary">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>}
    </div>
  );
}
